// @mui
import PropTypes from 'prop-types';
import { Card, Typography, Button, Box, Modal, Grid, IconButton } from '@mui/material';
import { useState, React } from 'react';
import { styled } from '@mui/material/styles';
// components
import { PokemonCard } from '../pokemons';
import Iconify from '../../../components/iconify';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'visible',
  padding: theme.spacing(2),
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
  },
  '&:hover .icon-wrapper': {
    transform: 'rotate(10deg) scale(1.1)',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(4),
    '&:hover': {
      transform: 'translateY(-8px) scale(1.02)',
    },
  },
}));

const StyledIconWrapper = styled(Box)(({ theme }) => ({
  width: 50,
  height: 50,
  margin: '0 auto 8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    width: 65,
    height: 65,
    margin: '0 auto 12px',
  },
  [theme.breakpoints.up('md')]: {
    width: 80,
    height: 80,
    margin: '0 auto 16px',
  },
}));

const StyledNumber = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: '1.5rem',
  marginBottom: theme.spacing(0.5),
  background: 'linear-gradient(135deg, currentColor 0%, currentColor 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  textShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  lineHeight: 1,
  [theme.breakpoints.up('sm')]: {
    fontSize: '2rem',
    marginBottom: theme.spacing(0.75),
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
  },
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'capitalize',
  letterSpacing: '0.5px',
  lineHeight: 1.2,
  [theme.breakpoints.up('sm')]: {
    fontSize: '0.875rem',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1rem',
  },
}));

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '90%' },
  maxWidth: '1200px',
  maxHeight: { xs: '95vh', sm: '90vh' },
  bgcolor: 'background.paper',
  borderRadius: { xs: '12px', sm: '16px' },
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  p: 0,
  overflow: 'hidden',
};

// ----------------------------------------------------------------------

export default function AppWidgetSummary({ title, total, color = 'primary', sx, pokemons, ...other }) {
  const icons = title.toLowerCase();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const isLoading = total === 0;

  return (
    <>
      <Button
        onClick={handleOpen}
        disabled={isLoading}
        sx={{
          p: 0,
          width: '100%',
          textTransform: 'none',
          '&:hover': { bgcolor: 'transparent' },
        }}
      >
        <StyledCard
          color={color}
          sx={{
            width: '100%',
            color: (theme) => theme.palette[color].darker,
            bgcolor: (theme) => theme.palette[color].lighter,
            ...sx,
          }}
          {...other}
        >
          <StyledIconWrapper
            className="icon-wrapper"
            sx={{
              animation: isLoading ? 'spin 2s linear infinite' : 'none',
            }}
          >
            <img
              src={`/assets/icons/type/${icons}.svg`}
              alt={title}
              style={{ width: '100%', height: '100%', filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))' }}
            />
          </StyledIconWrapper>

          {!isLoading && (
            <>
              <StyledNumber>{total}</StyledNumber>
              <StyledTitle sx={{ opacity: 0.85 }}>{title}</StyledTitle>
            </>
          )}
        </StyledCard>
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="pokemon-modal-title"
        aria-describedby="pokemon-modal-description"
      >
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              p: { xs: 2, sm: 2.5, md: 3 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5, md: 2 } }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 42, md: 48 },
                  height: { xs: 36, sm: 42, md: 48 },
                  borderRadius: '12px',
                  bgcolor: (theme) => theme.palette[color].lighter,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img 
                  src={`/assets/icons/type/${icons}.svg`} 
                  alt={title} 
                  style={{ 
                    width: '70%', 
                    height: '70%' 
                  }} 
                />
              </Box>
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    textTransform: 'capitalize',
                    fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {title} Type
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {total} Pokémon{total > 1 ? 's' : ''} found
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={handleClose} 
              sx={{ 
                color: 'text.secondary',
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
              }}
            >
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Box>

          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, overflowY: 'auto', maxHeight: 'calc(90vh - 100px)' }}>
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
              {pokemons?.map((pokemon, key) => (
                <Grid item xs={4} sm={6} md={4} lg={3} xl={2} key={key}>
                  <PokemonCard id={pokemon.id} name={pokemon.name} image={pokemon.image} types={pokemon.types} generation={pokemon.generation} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Modal>
    </>
  );
}

AppWidgetSummary.propTypes = {
  title: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  color: PropTypes.string,
  sx: PropTypes.object,
  pokemons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      types: PropTypes.array.isRequired,
      generation: PropTypes.string.isRequired,
    })
  ).isRequired,
};
